import { CartRecommendationsType } from '../../types';
import { defaultFetcher, FSLocale, localeLink } from '@fshr/shared';
import { getConfig } from '@fshr/shared-react';
import { GlobalStore } from '@fshr/stores';
import { useCallback, useEffect, useState } from 'react';

const buildQueryString = ({ currencyCode }: { currencyCode?: string }): string => {
	const queryString = new URLSearchParams();
	if (currencyCode) {
		queryString.set('currencyCode', currencyCode);
	}

	return queryString.toString() ? `?${queryString.toString()}` : '';
};

// Custom hook to fetch room upgrade and ancilliaires recos information
export function useGetRoomUpgradeAndAncilliariesRecos({
	profile,
	bookingId,
	locale = 'en',
	currencyCode,
	enableAnonGuestRecommedation = false,
	globalStateFetching = false,
}: {
	profile?: Pick<GlobalStore, 'profile'>['profile'];
	bookingId?: string;
	locale?: string;
	currencyCode?: string;
	enableAnonGuestRecommedation?: boolean;
	globalStateFetching?: boolean;
}): {
	data?: CartRecommendationsType;
	error?: unknown;
	isLoading: boolean;
} {
	const [recommendationsData, setRecommendationData] = useState<
		CartRecommendationsType | undefined
	>();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<unknown | undefined>();
	const { NEXT_PUBLIC_BFF_BASE_URL } = getConfig();

	// Function used to get room upgrade and ancilliaries recommendations
	// We are not using useSwr because we want avoid this call when no profile is provided ( user logedout or anonymous )
	const handleFetchUpgradeRoomAndAncillariesRecommendations = useCallback(async () => {
		if (!bookingId) {
			return;
		}
		if (globalStateFetching && !profile) {
			return;
		}
		if (!profile && !enableAnonGuestRecommedation) {
			return;
		}

		try {
			setIsLoading(true);
			const queryString = buildQueryString({ currencyCode });
			const apiResponse: CartRecommendationsType = await defaultFetcher(
				localeLink(locale as FSLocale)(
					`${NEXT_PUBLIC_BFF_BASE_URL}/cart/${bookingId}${queryString}`
				)
			);
			setRecommendationData(apiResponse);
		} catch (error) {
			setError(error);
		}
		setIsLoading(false);
	}, [
		NEXT_PUBLIC_BFF_BASE_URL,
		bookingId,
		locale,
		profile,
		currencyCode,
		enableAnonGuestRecommedation,
		globalStateFetching,
	]);

	useEffect(() => {
		handleFetchUpgradeRoomAndAncillariesRecommendations();
	}, [handleFetchUpgradeRoomAndAncillariesRecommendations]);

	return {
		data: recommendationsData,
		isLoading,
		error,
	};
}
